import {MDIcon} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {AnalyticsEventActions} from '@mintdata-wsp/common/controllers/analytics/AnalyticsEventActions';
import {PublishedApplicationResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/PublishedApplicationResponse';
import {Design, IDesignInfo, Project} from '@mintdata-wsp/common/state/DataState';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {DesignRef} from '@mintdata-wsp/common/state/ui/publish/PublishState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Column} from '@mintdata-wsp/wsp/ui/components/forms/Column';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import {DotsMenu} from '@mintdata-wsp/wsp/ui/components/menus/DotsMenu';
import {PublishDialogKind} from '@mintdata-wsp/wsp/ui/dialogs/publish/PublishDialog';
import {WspPage} from '@mintdata-wsp/wsp/ui/pages/workspace/WspPage';
import {paths} from '@mintdata-wsp/wsp/ui/routes/Routing';
import {MenuHandlerFn} from '@mintdata-wsp/wsp/ui/views/visualDesigner/general/menus/MainMenu';
import {DesignsFilter, DesignsFilterType} from '@mintdata-wsp/wsp/ui/views/workspace/home/filters/DesignsFilters';
import {IContextMenuItem} from '@mintdata/mint-kit/impl/components/contextMenu/ContextMenu';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import * as is from 'is_js';
import {computed} from 'mobx';
import {observer} from 'mobx-react';
import moment from 'moment';
import * as React from 'react';

const styles = require('./DesignsList.less');
const cx = classnames.bind(styles);

export interface DesignCardProps
{
    design:IDesignInfo;
    filterType:DesignsFilterType;
    viewAsApplication?:boolean;
    goToSpaceMenu?:boolean;
    highlightSearchTerm?:boolean;
}

@observer
export class DesignsListItem extends React.Component<DesignCardProps, any>
{
    @computed
    private get _isInDeletedDesignsIds():boolean
    {
        const {deletedDesignsIds} = rootState.ui.mainPage;
        const {design} = this.props;

        return deletedDesignsIds.indexOf(design.id) !== -1;
    }

    @computed
    private get _project():Project
    {
        const {design} = this.props;

        return rootState.data.projects.find(({id}) => id === design.projectId);
    }

    private _computeMenu():Array<IContextMenuItem>
    {
        const {design, viewAsApplication, goToSpaceMenu = true} = this.props;
        const {projects} = rootState.data;

        const newMenuItemFn = (label:string, handler?:MenuHandlerFn):IContextMenuItem => ({
            label,
            handler
        } as IContextMenuItem);

        const newSeparatorFn:() => IContextMenuItem = () => ({isSeparator: true});

        const designPublished = design.appInfo && design.appInfo.versions && design.appInfo.versions.length > 0;

        const publishDesignItem = newMenuItemFn('Publish', this._publishDesign);
        const renameDesignItem = newMenuItemFn('Rename', this._renameDesign);
        const duplicateDesignItem = newMenuItemFn('Duplicate', this._duplicateDesign);
        const deleteDesignItem = newMenuItemFn('Delete', this._deleteDesign);
        const unpublishDesignItem = newMenuItemFn('Unpublish');

        const openRelatedDesignItem = newMenuItemFn('Open related design', this._openRelatedDesign);
        const goToSpaceItem = newMenuItemFn('Go to space', this._goToSpace);

        const spaceItems = projects
            .filter(project => project.id !== design.projectId)
            .map(project =>
                newMenuItemFn(project.name, () => this._moveDesign(project))
            );
        const moveToSpaceItem = {
            label: 'Move to Space',
            dataSource: spaceItems
        };

        deleteDesignItem.isDisabled = !!designPublished;

        if( designPublished )
        {
            unpublishDesignItem.dataSource = design.appInfo.versions
                .sort(a => a.beta ? 1 : -1)
                .filter(v => !viewAsApplication ? true : v.beta === design.appVersion.beta)
                .map((version:PublishedApplicationResponse) =>
                {
                    const title = 'From ' + (version.beta ? 'staging' : 'production');
                    return newMenuItemFn(title, this._unpublishApplication(version));
                });
        }
        else
        {
            unpublishDesignItem.isDisabled = !designPublished;
        }

        const menu:Array<IContextMenuItem> = [];

        if( viewAsApplication )
        {
            menu.push(
                openRelatedDesignItem,
                goToSpaceItem,
                newSeparatorFn(),
                unpublishDesignItem,
            );
        }
        else
        {
            menu.push(
                publishDesignItem,
                newSeparatorFn(),
                renameDesignItem,
                duplicateDesignItem,
                newSeparatorFn()
            );

            if( goToSpaceMenu ) menu.push(goToSpaceItem);

            menu.push(
                moveToSpaceItem,
                deleteDesignItem,
                newSeparatorFn(),
                unpublishDesignItem
            );
        }

        return menu;
    }

    @bind
    private _openRelatedDesign()
    {
        const {design} = this.props;

        controllers.navigation.navigateTo(paths.design(design.projectId, design.id));
    }

    @bind
    private _goToSpace()
    {
        const {design} = this.props;

        controllers.navigation.navigateTo(paths.spaces(design.projectId));
    }

    @bind
    private _publishDesign()
    {
        const {design} = this.props;

        const designRef = new DesignRef(design.projectId, design.id, design.name, design.version, design.uuid);

        controllers.ui.publish.showPublishDialog({designRef: designRef, kind: PublishDialogKind.Full});
    }

    @bind
    private _unpublishApplication(version:PublishedApplicationResponse)
    {
        return () => controllers.ui.publishedApps.unpublish(version.id);
    }

    @bind
    private _renameDesign()
    {
        const {design} = this.props;

        controllers.data.designs.renameDesignDialog(this._project, design);
    }

    @bind
    private _duplicateDesign()
    {
        const {design} = this.props;

        const project = this._project;

        controllers.data.designs.duplicateDesign(project, design)
            .then((newDesign:Design) =>
            {
                const {currentOrganization} = rootState.userSession;

                controllers.analytics.main.trackEvent(
                    AnalyticsEventActions.WSP_DESIGN_DUPLICATED,
                    {
                        org_sid: currentOrganization.siteId,
                        org_name: currentOrganization.name,

                        space_id: project.id,
                        space_name: project.name,

                        design_id: newDesign.id,
                        design_name: newDesign.name
                    }
                );
            });
    }

    @bind
    private _deleteDesign()
    {
        const {design} = this.props;

        controllers.data.designs.deleteDesignDialog(this._project, design).then(() =>
        {
            controllers.ui.dialogs.hideConfirmDialog();
        });
    }

    @bind
    async _moveDesign(toProject:Project)
    {
        const {design} = this.props;
        controllers.data.designs.moveDesign(this._project, design, toProject);
    }

    @bind
    private _onClickDesignName(event:React.SyntheticEvent<any>)
    {
        event.preventDefault();

        const {viewAsApplication} = this.props;

        if( viewAsApplication )
            this._openApplication();
        else
            this._openDesign(event);
    }

    @bind
    private _onClickImage(event:React.SyntheticEvent<any>)
    {
        event.preventDefault();

        const {viewAsApplication} = this.props;

        if( !viewAsApplication )
        {
            // if( is.not.desktop() )
            this._openDesign(event);
            return;
        }

        this._openApplication();
    }

    @bind
    private _onDoubleClickImage(event:React.SyntheticEvent<any>)
    {
        event.preventDefault();

        const {viewAsApplication} = this.props;

        if( viewAsApplication ) return;

        this._openDesign(event);
    }

    // private _openDesignOnMobileDialog = () =>
    // {
    //     const {design} = this.props;
    //
    //     const props:IConfirmDialogProps = {
    //         title: 'To Edit An Application',
    //         content: (
    //             <Column className={styles.openOnMobile}>
    //                 <div>
    //                     Please open the <b>{design.name}</b> design in a desktop browser.
    //                 </div>
    //                 <br />
    //                 On this device, you can always read the documentation.
    //             </Column>
    //         ),
    //         buttonSet: [null, 'Read documentation'],
    //         onConfirmed: () => window.open('https://mintdata.com/docs/learn/'),
    //     };
    //
    //     controllers.ui.dialogs.showDialog(Dialogs.Confirm, false, props);
    // };

    private _openDesign = (event?:React.SyntheticEvent<any>) =>
    {
        const {design} = this.props;

        if( is.not.desktop() )
        {
            // this._openDesignOnMobileDialog();
            WspPage.openDesignOnMobileDialog(design.name);
            return;
        }

        const project = this._project;

        const {currentOrganization} = rootState.userSession;

        controllers.analytics.main.trackEvent(
            AnalyticsEventActions.WSP_DESIGN_OPEN_CLICKED,
            {
                org_sid: currentOrganization.siteId,
                org_name: currentOrganization.name,

                space_id: project.id,
                space_name: project.name,

                design_id: design.id,
                design_name: design.name
            }
        );

        controllers.navigation.toDesign(project.id, design.id, event && !(event as React.MouseEvent<any>).altKey);
    };

    private _openApplication = () =>
    {
        const {design} = this.props;

        const {appVersion} = design;
        if( !appVersion ) return;

        const {urlPart, beta} = appVersion;
        controllers.navigation.toPublishedApp(urlPart, beta);
    };

    private _getHighlightedDesignName(name:string):JSX.Element
    {
        const {searchString} = rootState.ui.mainPage;

        const highlightPos = name.toLowerCase().indexOf(searchString.toLowerCase());

        const firstPart = name.substr(0, highlightPos);
        const highlightedPart = name.substr(highlightPos, searchString.length);
        const lastPart = name.substr(highlightPos + searchString.length);
        
        return <span>
            {firstPart}
            <mark className={cx('highlight')}>
                {highlightedPart}
            </mark>
            {lastPart}
        </span>
    }

    @bind
    private _onPublishDesignClicked()
    {
        const {design} = this.props;

        const designRef = new DesignRef(design.projectId, design.id, design.name, design.version, design.uuid);

        controllers.ui.publish.showPublishDialog({designRef: designRef, kind: PublishDialogKind.Full});
    }

    private _renderImage = ():JSX.Element =>
    {
        const {design} = this.props;

        let coverUrlNoExt;
        let coverImageType;

        if( design.coverUrl )
        {
            coverUrlNoExt = design.coverUrl.substr(0, design.coverUrl.lastIndexOf('.')) || design.coverUrl;
            const coverImageExt = design.coverUrl.replace(coverUrlNoExt + '.', '');

            if( coverImageExt === 'png' )
            {
                coverImageType = 'image/png';
            }
            else if( coverImageExt === 'jpg' )
            {
                coverImageType = 'image/jpeg';
            }
        }

        // const imgWidth = Math.ceil(Math.random() * 700) + 200;
        // const imgHeight = Math.ceil(Math.random() * 200) + 250;

        return (
            <div
                className={styles.imgWrapper}
                onClick={this._onClickImage}
                // onDoubleClick={this._onDoubleClickImage}
            >
                {
                    (design.coverUrl && coverImageType) &&
                    <picture className={styles.picture}>
                        <source srcSet={coverUrlNoExt + '.webp'} type={coverImageType} />
                        <img
                            className={styles.img}
                            src={design.coverUrl}
                            alt="design cover"
                            // src={`https://via.placeholder.com/${imgWidth}x${imgHeight}.png`}
                        />
                    </picture>
                }
            </div>
        );
    };

    private _onClickSlot = (version:PublishedApplicationResponse) =>
    {
        return (e:React.MouseEvent<HTMLElement>) =>
        {
            e.stopPropagation();

            const {urlPart, beta} = version;
            controllers.navigation.toPublishedApp(urlPart, beta);
        };
    };

    private _renderAppSlot = (version:PublishedApplicationResponse):JSX.Element =>
    {
        const {viewAsApplication} = this.props;

        const {urlPart, beta} = version;

        const title:string = beta ? 'Staging' : 'Production';

        return (
            <Row
                key={urlPart + beta}
                className={cx(styles.appType, beta ? styles.appTypeStaging : styles.appTypeProd, {isOpened: viewAsApplication})}
                onClick={this._onClickSlot(version)}
            >
                <span className={styles.appTypeText}>
                    {title}
                </span>
                <Row className={styles.appTypeIconWrapper}>
                    <MDIcon
                        className={styles.appTypeIcon}
                        name={MDIcons.common.OpenInNew}
                    />
                </Row>
            </Row>
        );
    };

    private _renderAppInfo = ():JSX.Element =>
    {
        const {design, viewAsApplication} = this.props;

        if( !design.appInfo ) return null;

        const viewAsDesign = !viewAsApplication;

        return (
            <Column className={styles.appTypes}>
                {
                    (viewAsApplication && design.appVersion) &&
                    this._renderAppSlot(design.appVersion)
                }
                {
                    (viewAsDesign && design.appInfo.versions) &&
                    design.appInfo.versions
                        .sort(a => a.beta ? 1 : -1)
                        .map(this._renderAppSlot)
                }
            </Column>
        );
    };

    render()
    {
        const {design, viewAsApplication, filterType, highlightSearchTerm} = this.props;

        const designsFilter = controllers.ui.mainPage.getDesignsFilter(filterType);

        const menuItems = this._computeMenu();

        // const isInDeletedDesignsIds = this._isInDeletedDesignsIds;

        const {createdOn = 0, modifiedOn = 0, accessedOn = 0} = design;

        let timeActionText:string;
        let maxTime:number;

        if( viewAsApplication )
        {
            timeActionText = 'Published';

            maxTime = design.appVersion.publishedAt;
        }
        else if( designsFilter === DesignsFilter.Published )
        {
            timeActionText = 'Published';

            const allAppTimes:number[] = design.appInfo.versions.map(v => v.publishedAt);
            maxTime = Math.max.apply(null, allAppTimes);
        }
        else
        {
            timeActionText = modifiedOn > createdOn || accessedOn > createdOn ? 'Edited' : 'Created';

            maxTime = Math.max(createdOn || 0, modifiedOn || 0, accessedOn || 0);
        }

        const timeAgo:number = maxTime - Date.now();
        const timeAgoText:string = moment.duration(timeAgo < 0 ? timeAgo : -timeAgo).humanize(true);

        return (
            <div
                // key={design.id}
                className={styles.design}
            >
                {this._renderImage()}

                <div className={styles.footer}>
                    <div className={styles.content}>
                        <a
                            href={paths.design(this._project.id, design.id)}
                            className={styles.name}
                            onClick={this._onClickDesignName}
                        >
                            {highlightSearchTerm ? this._getHighlightedDesignName(design.name) : design.name}
                        </a>
                        <div className={styles.time}>
                            {timeActionText} {timeAgoText}
                        </div>
                    </div>
                    <div className={styles.menu}>
                        <DotsMenu
                            menuItems={menuItems}
                            className={styles.dotsMenu}
                            popupClassName={styles.dotsMenuPopup}
                        />
                    </div>
                    {/*{*/}
                    {/*    publishedDesignResponse && publishedDesignResponse.versions && publishedDesignResponse.versions.map(version =>*/}
                    {/*    <PublishedAppSlot publishedApp={version} key={version.urlPart + version.beta} />*/}
                    {/*)}*/}
                </div>
                {
                    viewAsApplication &&
                    <div className={styles.iconApplication} />
                }
                {this._renderAppInfo()}
            </div>
        );
    }
}
